import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage, isContractMismatch } from '../api/client.ts';
import { Button } from './primitives.tsx';

/**
 * The two states every screen that talks to the API has to render, in one place.
 *
 * `FailureNotice` distinguishes a *contract* failure — the endpoint is missing, or its response
 * changed shape — from an ordinary error. That distinction is load-bearing right now rather than
 * theoretical: the auth endpoints this app calls are being built in parallel, and the honest
 * report for "there is no such route yet" is not "something went wrong". Collapsing the two is how
 * a half-built backend gets debugged as a frontend bug.
 */

export function LoadingNotice({ label }: { readonly label: string }) {
  return (
    // `aria-live="polite"` rather than a spinner alone: a screen-reader user gets told that
    // something is in progress, and gets told once, rather than nothing happening audibly.
    <p role="status" aria-live="polite" className="m-0 p-6 text-center text-sm text-ink-muted">
      {label}
    </p>
  );
}

export function FailureNotice({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry?: () => void;
}) {
  const { t } = useTranslation('common');
  const contractProblem = isContractMismatch(error);

  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-panel border border-danger bg-surface p-4"
    >
      <h2 className="m-0 text-base font-semibold text-ink">
        {contractProblem
          ? t('asyncState.failureNotice.contractMismatchHeading')
          : t('asyncState.failureNotice.genericHeading')}
      </h2>
      <p className="m-0 text-sm text-ink">{errorMessage(error)}</p>
      {contractProblem ? (
        <p className="m-0 text-sm text-ink-muted">
          {t('asyncState.failureNotice.contractMismatchExplanation')}
        </p>
      ) : null}
      {onRetry !== undefined ? (
        <Button variant="secondary" onClick={onRetry} className="self-start">
          {t('asyncState.failureNotice.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Loading / error / (optionally) empty / content — the one state chain a page with a single
 * primary query needs, generic over the query's data type so a caller's `children` render prop
 * gets it narrowed to the success case with no `data`-is-possibly-`undefined` check at the call
 * site.
 *
 * Extracted from `routes/PlatformPage.tsx`, which had already written this once as a file-local
 * component (its own header comment named `PracticesSection`/`AuditLogSection`/`OperatorsSection`
 * as three identical hand-rolled ternaries this replaced) — `routes/PracticeBrandingPage.tsx`
 * needing the identical shape a second time is `claude-kit/rules/invariants.md`'s "favor the
 * general, reusable solution once a pattern actually recurs" trigger: a second genuine call site is
 * exactly the point to extract, not add a second file-local copy.
 *
 * `isEmpty`/`emptyLabel` are optional as a pair, not required — some callers (an admin toggle, a
 * settings form) have no distinct empty state to render at all.
 */
export function AsyncSection<T>({
  query,
  loadingLabel,
  isEmpty,
  emptyLabel,
  children,
}: {
  // The *whole* `UseQueryResult<T>`, not a `Pick` of individual fields: `UseQueryResult` is a
  // discriminated union (a `data`/`error` pair whose types correlate with `isPending`/`isError`),
  // and `Pick` over a union does not distribute — it collapses that correlation, which is exactly
  // why `query.data` below needs to type-check as `T` rather than `T | undefined` once `isPending`
  // and `isError` have both been ruled out.
  readonly query: UseQueryResult<T>;
  readonly loadingLabel: string;
  readonly isEmpty?: (data: T) => boolean;
  readonly emptyLabel?: string;
  readonly children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingNotice label={loadingLabel} />;
  if (query.isError) {
    return (
      <FailureNotice
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (isEmpty?.(query.data) === true) {
    return <p className="m-0 text-sm text-ink">{emptyLabel}</p>;
  }
  return <>{children(query.data)}</>;
}
